import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Trophy, Zap, Target, Flame, ArrowUpRight } from "lucide-react";

/** Public read of a user's weekly wrapped: /w/:username. Fully shareable, SEO-friendly. */
export default function WeeklyWrappedPublic() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    document.title = `@${username}'s week · ExamBro`;
    api.get(`/wrapped/${username}`)
      .then((r) => { setData(r.data); setState("ok"); })
      .catch((e) => setState(e?.response?.status === 403 ? "private" : "notfound"));
  }, [username]);

  if (state === "loading") return <div className="min-h-screen flex items-center justify-center text-stone-500 font-mono text-sm">Loading…</div>;
  if (state !== "ok") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-stone-500">
        <div className="font-heading text-3xl font-bold text-stone-900 mb-2">
          {state === "private" ? "This wrap is private" : "Not found"}
        </div>
        <Link to="/" className="text-orange-600 mt-4 text-sm">← Back to ExamBro</Link>
      </div>
    );
  }

  const u = data.user || {};

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <div className="border-b border-stone-800">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading text-lg font-bold text-white">
            <div className="h-6 w-6 rounded bg-orange-600 flex items-center justify-center text-white text-xs font-bold">E</div>
            <span>ExamBro</span>
          </Link>
          <Link to={`/u/${u.username}`} className="text-sm text-orange-400 hover:text-orange-300 inline-flex items-center gap-1">
            Full profile <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-400 mb-2">
            Weekly Wrapped
          </div>
          <h1 className="font-heading text-5xl font-bold tracking-tight">@{u.username}</h1>
          {u.name && <div className="text-stone-400 mt-1">{u.name}</div>}
        </div>

        {data.count === 0 ? (
          <Card className="p-16 rounded-lg border-stone-800 bg-stone-900 text-center">
            <p className="text-stone-400">No mocks this week yet.</p>
          </Card>
        ) : (
          <Card
            className="p-10 rounded-lg border-0 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0c0a09 0%, #1c1917 45%, #7c2d12 100%)",
            }}
          >
            <div
              className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 30%, #f97316 0.5px, transparent 1px), radial-gradient(circle at 80% 70%, #f97316 0.5px, transparent 1px)",
                backgroundSize: "60px 60px, 40px 40px",
              }}
            />
            <div className="relative">
              <div className="grid grid-cols-2 gap-8 mb-8">
                <BigStat label="Mocks" v={data.count} c="#f97316" icon={Trophy} />
                <BigStat label="Focus time" v={`${data.total_focus_minutes}m`} c="#fb923c" icon={Zap} />
                <BigStat label="Avg accuracy" v={`${data.avg_accuracy}%`} c="#10b981" icon={Target} />
                <BigStat label="Study days" v={data.unique_days} c="#e11d48" icon={Flame} />
              </div>

              {data.best_attempt && (
                <div className="pt-6 border-t border-stone-800">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400 mb-1">
                    Best mock this week
                  </div>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-heading text-lg font-semibold truncate">
                      {data.best_attempt.exam_title}
                    </div>
                    <div className="font-heading text-4xl font-bold tabular-nums text-orange-500">
                      {data.best_attempt.score_pct}%
                    </div>
                  </div>
                </div>
              )}

              {data.best_section && (
                <div className="mt-4">
                  <Badge className="bg-emerald-900/40 text-emerald-300 border-emerald-800 font-mono text-[10px] uppercase tracking-[0.2em]">
                    Sharpest section · {data.best_section}
                  </Badge>
                </div>
              )}
            </div>
          </Card>
        )}

        <div className="mt-8 text-center">
          <div className="text-sm text-stone-500 mb-3">Want your own weekly wrap?</div>
          <Link to="/login">
            <button className="rounded-md bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium px-4 py-2">
              Start free — 7 mocks/month
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function BigStat({ label, v, c, icon: Icon }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4" style={{ color: c }} />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">{label}</span>
      </div>
      <div className="font-heading text-5xl font-bold tabular-nums" style={{ color: c }}>{v}</div>
    </div>
  );
}
